import type { MessageInputRef } from "@agent-ui/features/chat/components/chat-elements/message-input/MessageInput";
import type { Editor } from "@tiptap/react";
import type { LottieRefCurrentProps } from "lottie-react";

import React, { useRef, useState } from "react";

import SvgMessages from "@svg/messages";

import { IconWrapper } from "@agent-ui/features/chat/components/chat-elements/IconWrapper";
import {
  PAGE_PLACEHOLDER_OPTIONS,
  PLACEHOLDER_OPTIONS,
} from "@agent-ui/features/chat/components/chat-elements/placeholder/placeholderOptions";
import { useQuickPrompt } from "@agent-ui/features/chat/hooks/useQuickPrompt";
import twMerge from "@replo/design-system/utils/twMerge";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import Lottie from "lottie-react";
import { useSearchParams } from "react-router-dom";

const noopAudio = {
  play: () => {},
  pause: () => {},
  seek: () => {},
  playing: () => {},
  rate: () => {},
  setVolume: () => {},
};

const PromptCardIcon = ({
  animationData,
  iconClassName,
  lottieClassName,
  isHovered,
  onLoad,
}: {
  animationData: unknown;
  iconClassName?: string;
  lottieClassName?: string;
  isHovered: boolean;
  onLoad?: () => void;
}) => {
  const lottieRef = useRef<LottieRefCurrentProps | null>(null);
  const wasHoveredRef = useRef(false);

  React.useEffect(() => {
    if (isHovered && !wasHoveredRef.current) {
      lottieRef.current?.play();
    } else if (!isHovered && wasHoveredRef.current) {
      lottieRef.current?.stop();
    }
    wasHoveredRef.current = isHovered;
  }, [isHovered]);

  return (
    <div
      className={twMerge(
        "mix-blend-multiply w-16 h-16 -mb-3 overflow-hidden",
        iconClassName,
      )}
    >
      <Lottie
        lottieRef={lottieRef}
        animationData={animationData}
        className={twMerge("w-16 h-16 -ml-1 -mt-1", lottieClassName)}
        autoplay={false}
        loop={false}
        onDOMLoaded={onLoad}
        audioFactory={() => noopAudio}
      />
    </div>
  );
};

type PlaceholderOption =
  | {
      animationData: unknown;
      iconClassName?: string;
      lottieClassName?: string;
      title: string;
      subtitle: string;
      prompt: string;
      action?: string;
    }
  | {
      icon: React.ReactNode;
      title: string;
      subtitle: string;
      prompt: string;
      action?: string;
    };

const PlaceholderOptionCard = ({
  option,
  onClick,
  onLottieLoad,
}: {
  option: PlaceholderOption;
  onClick: () => void;
  onLottieLoad?: () => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const hasAnimationData = "animationData" in option;

  return (
    <div
      className="flex flex-col border-[0.5px] border-border rounded-[10px] p-3 hover:shadow-xs cursor-pointer select-text relative transition-shadow"
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="mb-0.5">
        {hasAnimationData ? (
          <PromptCardIcon
            animationData={option.animationData}
            iconClassName={option.iconClassName}
            lottieClassName={option.lottieClassName}
            isHovered={isHovered}
            onLoad={onLottieLoad}
          />
        ) : (
          <IconWrapper size="base">{option.icon}</IconWrapper>
        )}
      </div>
      <div className="text-sm font-medium leading-5 text-default truncate mb-1">
        {option.title}
      </div>
      <div className="text-sm leading-5 text-placeholder">
        {option.subtitle}
      </div>
    </div>
  );
};

const PlaceholderView = ({
  editor,
  messageInputRef,
}: {
  editor: Editor | null;
  messageInputRef: React.RefObject<MessageInputRef>;
}) => {
  const [searchParams] = useSearchParams();
  const logEvent = useLogAnalytics();
  const pageId = searchParams.get("pageId");
  const currentPlaceholderOptions: PlaceholderOption[] = pageId
    ? PAGE_PLACEHOLDER_OPTIONS
    : PLACEHOLDER_OPTIONS;

  const onQuickPrompt = useQuickPrompt({
    editor,
    onSelectUploadImage: () => {
      void messageInputRef.current?.triggerFileUpload();
    },
    onSelectProduct: () => {
      void messageInputRef.current?.triggerContextMenu();
    },
  });

  return (
    <div className="flex flex-col flex-1 w-full items-center justify-center relative">
      <SvgMessages className="h-12 w-auto hidden md:block" />
      <div className="header-h4 mt-1">Perfect your page</div>
      <div className="text-sm text-muted mb-7">
        Some quick actions to get you started
      </div>
      <div className="grid grid-cols-2 gap-3 relative">
        {currentPlaceholderOptions.map((option, index) => (
          <PlaceholderOptionCard
            key={index}
            option={option}
            onClick={() => {
              logEvent("chat.selectRecommendationPrompt", {
                origin: "chat",
                prompt: option.prompt,
              });

              onQuickPrompt(option.prompt, option.action);
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default PlaceholderView;
